import { useState, useRef, useEffect } from 'react';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon, PencilIcon, TrashIcon,
  DocumentArrowUpIcon, ArrowDownTrayIcon, DocumentIcon,
  PlusIcon, LockClosedIcon, EnvelopeIcon, CalendarIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { getClient, updateClient, deleteClient, inviteClient, exportFhir } from '../services/clientService';
import { createSetupIntent, saveCard, removeCard } from '../services/clientPaymentService';
import { getDocuments, uploadDocument, downloadDocument, deleteDocument } from '../services/documentService';
import { getNotes } from '../services/noteService';
import { getAppointments, createAppointment } from '../services/appointmentService';
import { getInvoices } from '../services/invoiceService';
import { getClaims } from '../services/insuranceService';
import { getEligibilityChecks } from '../services/eligibilityService';
import { getClientAssessmentResponses } from '../services/intakeFormService';
import { getTreatmentPlans, deleteTreatmentPlan } from '../services/treatmentPlanService';
import Badge from '../components/common/Badge';
import OutcomeTrendChart from '../components/common/OutcomeTrendChart';
import Spinner from '../components/common/Spinner';
import Modal from '../components/common/Modal';
import Drawer from '../components/common/Drawer';
import EmptyState from '../components/common/EmptyState';
import SkeletonBlock from '../components/common/SkeletonBlock';
import ClientForm from '../components/features/clients/ClientForm';
import AppointmentForm from '../components/features/appointments/AppointmentForm';
import EligibilityCheckModal from '../components/insurance/EligibilityCheckModal';
import PayerCombobox from '../components/insurance/PayerCombobox';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'appointments', label: 'Appointments' },
  { id: 'billing', label: 'Billing' },
  { id: 'insurance', label: 'Insurance' },
  { id: 'notes', label: 'Notes' },
  { id: 'treatment_plans', label: 'Treatment Plans' },
  { id: 'assessments', label: 'Assessments' },
  { id: 'files', label: 'Files' },
];

const STATUS_VARIANTS = {
  draft: 'default',
  sent: 'info',
  paid: 'success',
  partially_paid: 'warning',
  overdue: 'error',
  cancelled: 'default',
};

const APPT_VARIANTS = {
  scheduled: 'info',
  confirmed: 'success',
  completed: 'success',
  cancelled: 'default',
  no_show: 'warning',
  rescheduled: 'warning',
};

const fmt = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val ?? 0);

const insuranceProfileSchema = z.object({
  insurancePayerName: z.string().max(255).optional().or(z.literal('')),
  insurancePayerEdiId: z.string().max(50).optional().or(z.literal('')),
  insurancePlanName: z.string().max(200).optional().or(z.literal('')),
  insuranceMemberId: z.string().max(100).optional().or(z.literal('')),
  insuranceGroupNumber: z.string().max(100).optional().or(z.literal('')),
  insuranceSubscriberName: z.string().max(200).optional().or(z.literal('')),
  insuranceSubscriberDob: z.string().optional().or(z.literal('')),
  insuranceSubscriberRelationship: z.string().max(50).optional().or(z.literal('')),
  smsRemindersOptedIn: z.boolean().optional(),
});

const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-sky-100 text-sky-700',
];
function getAvatarColor(id) {
  const hash = (id || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

const InfoRow = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">{label}</dt>
    <dd className="mt-1 text-sm text-neutral-900 dark:text-neutral-100">{value || '—'}</dd>
  </div>
);

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [isEligibilityOpen, setIsEligibilityOpen] = useState(false);
  const [addingCard, setAddingCard] = useState(false);
  const [cardError, setCardError] = useState(null);
  const cardMountRef = useRef(null);
  const stripeRef = useRef(null);
  const cardElementRef = useRef(null);
  const fileInputRef = useRef(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['client', id],
    queryFn: () => getClient(id),
  });

  const updateMutation = useMutation({
    mutationFn: (formData) => updateClient(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setIsEditOpen(false);
      toast.success('Client updated');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to update client');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client archived');
      navigate('/clients');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to archive client');
    },
  });

  const inviteMutation = useMutation({
    mutationFn: () => inviteClient(id),
    onSuccess: () => toast.success('Portal invite sent'),
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to send invite'),
  });

  const [fhirExporting, setFhirExporting] = useState(false);
  const handleFhirExport = async () => {
    setFhirExporting(true);
    try {
      const bundle = await exportFhir(id);
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/fhir+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `client-${id}-fhir.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('FHIR export failed');
    } finally {
      setFhirExporting(false);
    }
  };

  // --- Documents ---
  const { data: docsData, isLoading: docsLoading } = useQuery({
    queryKey: ['documents', id],
    queryFn: () => getDocuments(id),
    enabled: activeTab === 'files',
  });

  const uploadMutation = useMutation({
    mutationFn: (file) => uploadDocument(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', id] });
      toast.success('Document uploaded');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Upload failed');
    },
  });

  const deleteDocMutation = useMutation({
    mutationFn: (docId) => deleteDocument(id, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', id] });
      setDocToDelete(null);
      toast.success('Document deleted');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to delete document');
    },
  });

  // --- Notes ---
  const { data: notesData } = useQuery({
    queryKey: ['notes', { clientId: id }],
    queryFn: () => getNotes({ clientId: id, limit: 10 }),
    enabled: activeTab === 'notes',
  });
  const sessionNotes = notesData?.notes ?? [];

  // --- Assessments ---
  const { data: assessmentsData } = useQuery({
    queryKey: ['assessmentResponses', id],
    queryFn: () => getClientAssessmentResponses(id),
    enabled: activeTab === 'assessments',
  });
  const assessmentResponses = assessmentsData?.responses ?? [];

  // --- Treatment Plans ---
  const { data: treatmentPlansData, isLoading: plansLoading } = useQuery({
    queryKey: ['treatmentPlans', id],
    queryFn: () => getTreatmentPlans(id),
    enabled: activeTab === 'treatment_plans',
  });
  const treatmentPlans = treatmentPlansData ?? [];

  const deletePlanMutation = useMutation({
    mutationFn: (planId) => deleteTreatmentPlan(planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['treatmentPlans', id] });
      toast.success('Treatment plan deleted');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete'),
  });

  // --- Appointments ---
  const { data: apptData, isLoading: apptLoading } = useQuery({
    queryKey: ['appointments', { clientId: id }],
    queryFn: () => getAppointments({ clientId: id, limit: 50 }),
    enabled: activeTab === 'appointments',
  });
  const appointments = apptData?.appointments ?? [];

  const scheduleMutation = useMutation({
    mutationFn: (formData) => createAppointment(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', { clientId: id }] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setIsScheduleOpen(false);
      toast.success('Appointment scheduled');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to schedule appointment');
    },
  });

  // --- Card on File ---
  const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  const removeCardMut = useMutation({
    mutationFn: () => removeCard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      toast.success('Card removed');
    },
    onError: () => toast.error('Failed to remove card'),
  });

  const handleAddCard = async () => {
    if (!stripeKey) return toast.error('Stripe is not configured');
    setCardError(null);
    setAddingCard(true);
    // Mount Stripe Elements after state update
  };

  useEffect(() => {
    if (!addingCard || !cardMountRef.current) return;
    let mounted = true;

    (async () => {
      try {
        // Load Stripe.js dynamically if not already loaded
        if (!window.Stripe) {
          await new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = 'https://js.stripe.com/v3/';
            s.onload = resolve;
            s.onerror = reject;
            document.head.appendChild(s);
          });
        }
        if (!mounted) return;
        stripeRef.current = window.Stripe(stripeKey);

        const { clientSecret } = await createSetupIntent(id);
        if (!mounted) return;

        const elements = stripeRef.current.elements();
        const card = elements.create('card', {
          style: {
            base: { fontSize: '14px', color: '#111827', '::placeholder': { color: '#9ca3af' } },
          },
        });
        card.mount(cardMountRef.current);
        cardElementRef.current = card;
        stripeRef.current._pendingClientSecret = clientSecret;
      } catch (err) {
        if (mounted) {
          setCardError(err.message || 'Failed to load card form');
          setAddingCard(false);
        }
      }
    })();

    return () => { mounted = false; };
  }, [addingCard]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmCard = async () => {
    if (!stripeRef.current || !cardElementRef.current) return;
    setCardError(null);
    try {
      const clientSecret = stripeRef.current._pendingClientSecret;
      const { setupIntent, error } = await stripeRef.current.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElementRef.current },
      });
      if (error) { setCardError(error.message); return; }
      await saveCard(id, setupIntent.payment_method);
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      toast.success('Card saved');
      setAddingCard(false);
      cardElementRef.current = null;
    } catch (err) {
      setCardError(err.response?.data?.message || err.message || 'Failed to save card');
    }
  };

  const handleCancelCard = () => {
    if (cardElementRef.current) { cardElementRef.current.destroy(); cardElementRef.current = null; }
    setAddingCard(false);
    setCardError(null);
  };

  // --- Invoices ---
  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices', { clientId: id }],
    queryFn: () => getInvoices({ clientId: id, limit: 50 }),
    enabled: activeTab === 'billing',
  });
  const invoices = invoicesData?.invoices ?? [];

  // --- Insurance ---
  const { data: claimsData, isLoading: claimsLoading } = useQuery({
    queryKey: ['claims', { clientId: id }],
    queryFn: () => getClaims({ clientId: id, limit: 50 }),
    enabled: activeTab === 'insurance',
  });
  const claims = claimsData?.claims ?? [];

  const { data: eligibilityData, isLoading: eligibilityLoading, refetch: refetchEligibility } = useQuery({
    queryKey: ['eligibility', { clientId: id }],
    queryFn: () => getEligibilityChecks({ clientId: id, limit: 10 }),
    enabled: activeTab === 'insurance',
  });
  const eligibilityChecks = eligibilityData?.checks ?? [];

  const insuranceForm = useForm({ resolver: zodResolver(insuranceProfileSchema) });
  const { field: payerNameField } = useController({ name: 'insurancePayerName', control: insuranceForm.control });
  const { field: payerEdiField } = useController({ name: 'insurancePayerEdiId', control: insuranceForm.control });
  const handleInsurancePayerSelect = ({ name, ediId }) => {
    insuranceForm.setValue('insurancePayerName', name);
    if (ediId != null) insuranceForm.setValue('insurancePayerEdiId', ediId);
  };

  useEffect(() => {
    if (data) {
      insuranceForm.reset({
        insurancePayerName: data.insurancePayerName ?? '',
        insurancePayerEdiId: data.insurancePayerEdiId ?? '',
        insurancePlanName: data.insurancePlanName ?? '',
        insuranceMemberId: data.insuranceMemberId ?? '',
        insuranceGroupNumber: data.insuranceGroupNumber ?? '',
        insuranceSubscriberName: data.insuranceSubscriberName ?? '',
        insuranceSubscriberDob: data.insuranceSubscriberDob ?? '',
        insuranceSubscriberRelationship: data.insuranceSubscriberRelationship ?? '',
        smsRemindersOptedIn: data.smsRemindersOptedIn ?? false,
      });
    }
  }, [data, insuranceForm]);

  const insuranceMutation = useMutation({
    mutationFn: (formData) => updateClient(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      toast.success('Insurance profile saved');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save insurance profile'),
  });

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate(file);
    e.target.value = '';
  };

  const handleDownload = async (doc) => {
    try {
      await downloadDocument(id, doc.id, doc.fileName);
    } catch {
      toast.error('Failed to download document');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <SkeletonBlock className="w-12 h-12 shrink-0" rounded="rounded-full" />
          <div className="space-y-2">
            <SkeletonBlock className="h-6 w-48" rounded="rounded" />
            <SkeletonBlock className="h-4 w-20" rounded="rounded-full" />
          </div>
        </div>
        <SkeletonBlock className="h-10 w-full" rounded="rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1,2,3,4].map((i) => <SkeletonBlock key={i} className="h-40" rounded="rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-20">
        <p className="text-danger-600 dark:text-danger-400">Failed to load client.</p>
        <Link to="/clients" className="text-primary-600 hover:underline text-sm mt-2 inline-block">
          Back to clients
        </Link>
      </div>
    );
  }

  const client = data?.client;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/clients" className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 transition-colors">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${getAvatarColor(client.id)}`}>
            {`${client.firstName?.[0] ?? ''}${client.lastName?.[0] ?? ''}`.toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {client.firstName} {client.lastName}
            </h1>
            <div className="mt-1">
              <Badge label={client.status} variant={client.status} />
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {client.user?.lastLogin ? (
            <span className="btn btn-secondary opacity-60 cursor-default" title="Client has logged in to the portal">
              <EnvelopeIcon className="h-4 w-4" />
              Portal active
            </span>
          ) : (
            <button
              onClick={() => inviteMutation.mutate()}
              disabled={inviteMutation.isPending || !client.email}
              title={!client.email ? 'Client needs an email address first' : undefined}
              className="btn btn-secondary disabled:opacity-50"
            >
              <EnvelopeIcon className="h-4 w-4" />
              {client.userId
                ? (inviteMutation.isPending ? 'Sending…' : 'Resend portal invite')
                : (inviteMutation.isPending ? 'Sending…' : 'Invite to portal')}
            </button>
          )}
          <button onClick={handleFhirExport} disabled={fhirExporting} className="btn btn-secondary disabled:opacity-50" title="Export FHIR R4 Bundle">
            <ArrowDownTrayIcon className="h-4 w-4" />
            {fhirExporting ? 'Exporting…' : 'FHIR export'}
          </button>
          <button onClick={() => setIsEditOpen(true)} className="btn btn-secondary">
            <PencilIcon className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={() => setIsDeleteOpen(true)}
            className="btn btn-secondary text-danger-600 hover:text-danger-700"
          >
            <TrashIcon className="h-4 w-4" />
            Archive
          </button>
        </div>
      </div>

      {/* Tabs — sticky, overflow-scroll on narrow screens */}
      <div className="sticky top-0 z-10 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700 mb-6 -mx-6 px-6">
        <nav className="-mb-px flex gap-1 overflow-x-auto scrollbar-hide">
          {TABS.map((tab) => {
            const counts = {
              notes: notesData?.pagination?.total,
              appointments: apptData?.pagination?.total ?? apptData?.appointments?.length,
              billing: invoicesData?.invoices?.length,
              insurance: claimsData?.claims?.length,
              files: docsData?.length,
              assessments: assessmentResponses?.length,
            };
            const count = counts[tab.id];
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`shrink-0 pb-3 pt-1 px-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                }`}
              >
                {tab.label}
                {count != null && count > 0 && (
                  <span className={`ml-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-semibold h-4 px-1.5 ${
                    activeTab === tab.id
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
        {/* Quick actions */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setIsScheduleOpen(true)} className="btn btn-secondary btn-sm">
            <CalendarIcon className="h-4 w-4" />
            Schedule appointment
          </button>
          <Link to={`/notes/new?clientId=${id}`} className="btn btn-secondary btn-sm">
            <PlusIcon className="h-4 w-4" />
            Write note
          </Link>
          <Link to={`/billing/new?clientId=${id}`} className="btn btn-secondary btn-sm">
            <PlusIcon className="h-4 w-4" />
            Create invoice
          </Link>
          <Link to={`/insurance/new?clientId=${id}`} className="btn btn-secondary btn-sm">
            <PlusIcon className="h-4 w-4" />
            New claim
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Personal Information</h2>
            <dl className="grid grid-cols-2 gap-4">
              <InfoRow label="First name" value={client.firstName} />
              <InfoRow label="Last name" value={client.lastName} />
              <InfoRow
                label="Date of birth"
                value={client.dateOfBirth ? format(new Date(client.dateOfBirth), 'MM/dd/yyyy') : null}
              />
              <InfoRow label="Gender" value={client.gender} />
            </dl>
          </div>

          <div className="card">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Contact</h2>
            <dl className="grid grid-cols-1 gap-4">
              <InfoRow label="Email" value={client.email} />
              <InfoRow label="Phone" value={client.phone} />
            </dl>
          </div>

          <div className="card">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Address</h2>
            <dl className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <InfoRow label="Address" value={client.addressLine1} />
              </div>
              {client.addressLine2 && (
                <div className="col-span-2">
                  <InfoRow label="Address line 2" value={client.addressLine2} />
                </div>
              )}
              <InfoRow label="City" value={client.city} />
              <InfoRow label="State" value={client.state} />
              <InfoRow label="ZIP" value={client.zip} />
            </dl>
          </div>

          <div className="card">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Emergency Contact</h2>
            <dl className="grid grid-cols-1 gap-4">
              <InfoRow label="Name" value={client.emergencyContactName} />
              <InfoRow label="Phone" value={client.emergencyContactPhone} />
              <InfoRow label="Relationship" value={client.emergencyContactRelationship} />
            </dl>
          </div>

          {/* Insurance Profile — compact, full width */}
          <div className="card md:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Insurance</h2>
              <span className="text-xs text-neutral-400 dark:text-neutral-500">Member ID and subscriber name are encrypted at rest</span>
            </div>
            <form
              onSubmit={insuranceForm.handleSubmit((d) => insuranceMutation.mutate(d))}
              className="space-y-3"
            >
              {/* Row 1: Payer (wide) + EDI ID */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Payer / Insurance company</label>
                  <PayerCombobox value={payerNameField.value} onChange={handleInsurancePayerSelect} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Payer EDI ID</label>
                  <input type="text" className="input font-mono text-sm" placeholder="Auto-filled" {...payerEdiField} />
                </div>
              </div>
              {/* Row 2: Plan / Member ID / Group # / Relationship */}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Plan name</label>
                  <input type="text" className="input text-sm" placeholder="e.g. PPO Gold" {...insuranceForm.register('insurancePlanName')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Member ID</label>
                  <input type="text" className="input font-mono text-sm" {...insuranceForm.register('insuranceMemberId')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Group number</label>
                  <input type="text" className="input font-mono text-sm" {...insuranceForm.register('insuranceGroupNumber')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Relationship</label>
                  <select className="input text-sm" {...insuranceForm.register('insuranceSubscriberRelationship')}>
                    <option value="">Self / Select…</option>
                    <option value="self">Self</option>
                    <option value="spouse">Spouse</option>
                    <option value="child">Child</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              {/* Row 3: Subscriber name + DOB (only shown when relationship isn't self) */}
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Subscriber name <span className="font-normal text-neutral-400">(if different)</span></label>
                  <input type="text" className="input text-sm" {...insuranceForm.register('insuranceSubscriberName')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Subscriber DOB</label>
                  <input type="date" className="input text-sm" {...insuranceForm.register('insuranceSubscriberDob')} />
                </div>
                <div className="flex items-end">
                  <button type="submit" disabled={insuranceMutation.isPending} className="btn btn-primary w-full text-sm disabled:opacity-60">
                    {insuranceMutation.isPending ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
              {/* SMS opt-in */}
              <label className="flex items-center gap-2 cursor-pointer select-none pt-0.5">
                <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...insuranceForm.register('smsRemindersOptedIn')} />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">Opt in to SMS appointment reminders</span>
                <span className="text-xs text-neutral-400 dark:text-neutral-500">(requires phone number on file)</span>
              </label>
            </form>
          </div>

          {client.notes && (
            <div className="card md:col-span-2">
              <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Internal Notes</h2>
              <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}
        </div>
        </div>
      )}

      {activeTab === 'appointments' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Appointments</h2>
            <button
              onClick={() => setIsScheduleOpen(true)}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              <CalendarIcon className="h-4 w-4" />
              Schedule appointment
            </button>
          </div>
          {apptLoading ? (
            <div className="flex justify-center py-6"><Spinner size="sm" /></div>
          ) : appointments.length === 0 ? (
            <EmptyState
              title="No appointments"
              description="Schedule the first appointment for this client."
            />
          ) : (
            <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
              {appointments.map((appt) => (
                <li key={appt.id} className="py-3 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {format(parseISO(appt.startTime), 'MMM d, yyyy')}
                      </span>
                      <span className="text-sm text-neutral-500 dark:text-neutral-400">
                        {format(parseISO(appt.startTime), 'h:mm a')}
                      </span>
                      {appt.durationMinutes && (
                        <span className="text-xs text-neutral-400 dark:text-neutral-500">
                          · {appt.durationMinutes} min
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {appt.appointmentType?.name && (
                        <span className="text-xs text-neutral-600 dark:text-neutral-400">
                          {appt.appointmentType.name}
                        </span>
                      )}
                      {appt.isTelehealth && (
                        <span className="text-xs text-primary-600 dark:text-primary-400">· Telehealth</span>
                      )}
                    </div>
                  </div>
                  <Badge label={appt.status?.replace('_', ' ')} variant={APPT_VARIANTS[appt.status] || 'default'} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {activeTab === 'billing' && (
        <div className="space-y-6">
        {/* Card on file */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Card on File</h2>
            {!addingCard && !client?.stripePaymentMethodId && stripeKey && (
              <button onClick={handleAddCard} className="btn btn-primary text-sm flex items-center gap-2">
                <PlusIcon className="h-4 w-4" />
                Add card
              </button>
            )}
          </div>

          {client?.stripePaymentMethodId ? (
            <div className="flex items-center justify-between bg-neutral-50 dark:bg-neutral-700/40 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <CreditCardIcon className="w-6 h-6 text-primary-500" />
                <div>
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 capitalize">
                    {client.cardBrand} ···· {client.cardLast4}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    Expires {client.cardExpMonth}/{client.cardExpYear}
                    {client.cardVerifiedAt && ` · Verified ${format(new Date(client.cardVerifiedAt), 'MMM d, yyyy')}`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => removeCardMut.mutate()}
                disabled={removeCardMut.isPending}
                className="text-sm text-danger-600 dark:text-danger-400 hover:underline disabled:opacity-50"
              >
                Remove
              </button>
            </div>
          ) : addingCard ? (
            <div className="space-y-3">
              <div
                ref={cardMountRef}
                className="border border-neutral-300 dark:border-neutral-600 rounded-lg px-3 py-3 bg-white dark:bg-neutral-800 min-h-[42px]"
              />
              {cardError && <p className="text-sm text-danger-600 dark:text-danger-400">{cardError}</p>}
              <div className="flex gap-2">
                <button onClick={handleConfirmCard} className="btn btn-primary text-sm">Save card</button>
                <button onClick={handleCancelCard} className="btn text-sm border border-neutral-300 dark:border-neutral-600">Cancel</button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              No card on file.{!stripeKey && ' Stripe is not configured.'}
            </p>
          )}
        </div>

        {/* Invoices */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Invoices</h2>
            <Link
              to={`/billing/new?clientId=${id}`}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              New invoice
            </Link>
          </div>
          {invoicesLoading ? (
            <div className="flex justify-center py-6"><Spinner size="sm" /></div>
          ) : invoices.length === 0 ? (
            <EmptyState
              title="No invoices"
              description="Create an invoice to bill this client."
            />
          ) : (
            <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
              {invoices.map((inv) => (
                <li key={inv.id}>
                  <Link
                    to={`/billing/${inv.id}`}
                    className="flex items-center gap-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 -mx-1 px-1 rounded transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        #{inv.invoiceNumber}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {inv.serviceDate ? format(parseISO(inv.serviceDate), 'MMM d, yyyy') : '—'}
                        {inv.dueDate ? ` · Due ${format(parseISO(inv.dueDate), 'MMM d, yyyy')}` : ''}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      {fmt(inv.totalAmount)}
                    </span>
                    <Badge label={inv.status?.replace('_', ' ')} variant={STATUS_VARIANTS[inv.status] || 'default'} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
        </div>
      )}

      {activeTab === 'insurance' && (
        <div className="space-y-6">
          {/* Eligibility Checks */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Eligibility & Benefits</h2>
              <button
                onClick={() => setIsEligibilityOpen(true)}
                className="btn btn-primary text-sm flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Check eligibility
              </button>
            </div>
            {eligibilityLoading ? (
              <div className="flex justify-center py-6"><Spinner size="sm" /></div>
            ) : eligibilityChecks.length === 0 ? (
              <EmptyState
                title="No eligibility checks"
                description="Run a check to verify this client's insurance coverage."
              />
            ) : (
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {eligibilityChecks.map((chk) => (
                  <li key={chk.id} className="py-3 flex items-start gap-4">
                    <span className={`mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      chk.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : chk.status === 'inactive' ? 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
                      : chk.status === 'error' ? 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
                      : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400'
                    }`}>
                      {chk.status}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{chk.payerName}</p>
                      {chk.planName && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">{chk.planName}</p>
                      )}
                      {chk.isActive && (
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                          {chk.deductibleIndividual != null && (
                            <span>Deductible: {fmt(chk.deductibleIndividual)}
                              {chk.deductibleIndividualRemaining != null && ` (${fmt(chk.deductibleIndividualRemaining)} remaining)`}
                            </span>
                          )}
                          {chk.copayAmount != null && <span>Copay: {fmt(chk.copayAmount)}</span>}
                          {chk.coinsurancePct != null && <span>Coinsurance: {chk.coinsurancePct}%</span>}
                        </div>
                      )}
                      {chk.errorMessage && (
                        <p className="text-xs text-danger-600 dark:text-danger-400 mt-0.5">{chk.errorMessage}</p>
                      )}
                    </div>
                    <span className="text-xs text-neutral-400 dark:text-neutral-500 flex-shrink-0">
                      {format(parseISO(chk.createdAt), 'MMM d, yyyy')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Claims */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Claims</h2>
              <Link
                to={`/insurance/new?clientId=${id}`}
                className="btn btn-primary text-sm flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                New claim
              </Link>
            </div>
            {claimsLoading ? (
              <div className="flex justify-center py-6"><Spinner size="sm" /></div>
            ) : claims.length === 0 ? (
              <EmptyState
                title="No claims"
                description="Create a claim to submit to this client's insurance."
              />
            ) : (
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {claims.map((claim) => (
                  <li key={claim.id}>
                    <Link
                      to={`/insurance/${claim.id}`}
                      className="flex items-center gap-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 -mx-1 px-1 rounded transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{claim.payerName}</p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          {claim.submittedDate ? format(parseISO(claim.submittedDate), 'MMM d, yyyy') : 'Not submitted'}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        {fmt(claim.billedAmount)}
                      </span>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        claim.status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : claim.status === 'denied' ? 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
                        : claim.status === 'submitted' || claim.status === 'pending' ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400'
                      }`}>
                        {claim.status}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Session Notes</h2>
            <Link
              to={`/notes/new?clientId=${id}`}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              New note
            </Link>
          </div>
          {sessionNotes.length === 0 ? (
            <EmptyState
              title="No session notes"
              description="Write a note to document a session with this client."
            />
          ) : (
            <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
              {sessionNotes.map((note) => (
                <li key={note.id}>
                  <Link
                    to={`/notes/${note.id}`}
                    className="flex items-start gap-3 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 -mx-1 px-1 rounded transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        {note.noteType && (
                          <span className="text-xs font-medium text-primary-600 uppercase tracking-wide">
                            {note.noteType.replace('_', ' ')}
                          </span>
                        )}
                        {note.isSigned && (
                          <LockClosedIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-400" title="Signed" />
                        )}
                      </div>
                      {note.title && (
                        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{note.title}</p>
                      )}
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                        {note.content.slice(0, 100)}{note.content.length > 100 ? '…' : ''}
                      </p>
                    </div>
                    <span className="text-xs text-neutral-400 dark:text-neutral-500 flex-shrink-0 mt-0.5">
                      {format(parseISO(note.createdAt), 'MMM d, yyyy')}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {(notesData?.pagination?.total ?? 0) > 10 && (
            <div className="mt-3 pt-3 border-t">
              <Link to={`/notes?clientId=${id}`} className="text-sm text-primary-600 hover:text-primary-700">
                View all {notesData.pagination.total} notes →
              </Link>
            </div>
          )}
        </div>
      )}

      {activeTab === 'treatment_plans' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Treatment Plans</h2>
            <Link
              to={`/treatment-plans/new?clientId=${id}`}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" /> New Plan
            </Link>
          </div>
          {plansLoading ? (
            <div className="flex justify-center py-8"><Spinner size="sm" /></div>
          ) : treatmentPlans.length === 0 ? (
            <EmptyState
              title="No treatment plans"
              description="Create a structured treatment plan with goals, diagnoses, and interventions."
            />
          ) : (
            <div className="space-y-3">
              {treatmentPlans.map((plan) => (
                <div key={plan.id} className="card flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/treatment-plans/${plan.id}`}
                        className="font-medium text-neutral-900 dark:text-neutral-100 hover:text-primary-600 dark:hover:text-primary-400"
                      >
                        {plan.title}
                      </Link>
                      <Badge
                        label={plan.status}
                        variant={{ draft: 'default', active: 'info', completed: 'success', discontinued: 'inactive' }[plan.status] || 'default'}
                      />
                      {plan.isLocked && (
                        <span className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
                          <LockClosedIcon className="h-3 w-3" /> Signed
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-4 text-xs text-neutral-500 dark:text-neutral-400">
                      {plan.startDate && <span>Started {format(parseISO(plan.startDate), 'MMM d, yyyy')}</span>}
                      {plan.reviewDate && <span>Review {format(parseISO(plan.reviewDate), 'MMM d, yyyy')}</span>}
                      <span>{plan.goals?.length ?? 0} goal{plan.goals?.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Link
                      to={`/treatment-plans/${plan.id}`}
                      className="btn btn-secondary text-xs"
                    >
                      {plan.isLocked ? 'View' : 'Edit'}
                    </Link>
                    {!plan.isLocked && (
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this treatment plan?')) deletePlanMutation.mutate(plan.id);
                        }}
                        className="p-1.5 text-neutral-400 hover:text-red-500 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'assessments' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Assessment History</h2>
            <Link to="/intake-forms" className="text-sm text-primary-600 hover:text-primary-700">
              Manage forms →
            </Link>
          </div>
          {assessmentResponses.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-6">
              No submitted assessments yet. Assign a PHQ-9, GAD-7, or other instrument from the Intake Forms page.
            </p>
          ) : (
            <div>
              <OutcomeTrendChart responses={assessmentResponses} />
              {/* Score history table */}
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide border-b border-neutral-100 dark:border-neutral-700">
                    <th className="pb-2 pr-4">Instrument</th>
                    <th className="pb-2 pr-4">Date</th>
                    <th className="pb-2 pr-4 text-center">Score</th>
                    <th className="pb-2">Interpretation</th>
                    <th className="pb-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
                  {assessmentResponses.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2 pr-4 font-medium text-neutral-900 dark:text-neutral-100">{r.form?.title || '—'}</td>
                      <td className="py-2 pr-4 text-neutral-500 dark:text-neutral-400">
                        {r.submittedAt ? format(parseISO(r.submittedAt), 'MMM d, yyyy') : '—'}
                      </td>
                      <td className="py-2 pr-4 text-center">
                        {r.score != null ? (
                          <span className="inline-flex items-center justify-center h-7 w-10 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-sm font-bold">
                            {r.score}
                          </span>
                        ) : (
                          <span className="text-neutral-400">—</span>
                        )}
                      </td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-400 text-sm">
                        {r.scoreInterpretation || <span className="text-neutral-400">—</span>}
                      </td>
                      <td className="py-2 text-right">
                        <Link
                          to={`/intake-forms/responses/${r.id}`}
                          className="text-xs text-primary-600 hover:text-primary-700"
                        >
                          View →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'files' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Documents</h2>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              <DocumentArrowUpIcon className="h-4 w-4" />
              {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,.xlsx,.txt"
              onChange={handleFileChange}
            />
          </div>

          {docsLoading ? (
            <div className="flex justify-center py-6"><Spinner size="sm" /></div>
          ) : !docsData?.length ? (
            <EmptyState
              title="No documents"
              description="Upload PDFs, images, or Word documents to attach them to this client."
            />
          ) : (
            <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
              {docsData.map((doc) => (
                <li key={doc.id} className="flex items-center gap-3 py-3">
                  <DocumentIcon className="h-8 w-8 text-neutral-300 dark:text-neutral-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{doc.fileName}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {formatBytes(doc.fileSize)}
                      {doc.uploader && ` · ${doc.uploader.firstName} ${doc.uploader.lastName}`}
                      {` · ${format(new Date(doc.createdAt), 'MMM d, yyyy')}`}
                    </p>
                    {doc.description && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">{doc.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleDownload(doc)}
                      className="p-1.5 text-neutral-400 dark:text-neutral-500 hover:text-primary-600 transition-colors"
                      title="Download"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDocToDelete(doc)}
                      className="p-1.5 text-neutral-400 dark:text-neutral-500 hover:text-danger-600 transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Edit Client Drawer */}
      <Drawer isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title="Edit Client">
        <ClientForm
          defaultValues={{
            firstName: client.firstName,
            lastName: client.lastName,
            email: client.email ?? '',
            phone: client.phone ?? '',
            dateOfBirth: client.dateOfBirth ?? '',
            gender: client.gender ?? '',
            addressLine1: client.addressLine1 ?? '',
            addressLine2: client.addressLine2 ?? '',
            city: client.city ?? '',
            state: client.state ?? '',
            zip: client.zip ?? '',
            emergencyContactName: client.emergencyContactName ?? '',
            emergencyContactPhone: client.emergencyContactPhone ?? '',
            emergencyContactRelationship: client.emergencyContactRelationship ?? '',
            notes: client.notes ?? '',
          }}
          onSubmit={(formData) => updateMutation.mutate(formData)}
          onCancel={() => setIsEditOpen(false)}
          isSubmitting={updateMutation.isPending}
        />
      </Drawer>

      {/* Schedule Appointment Modal */}
      <Modal isOpen={isScheduleOpen} onClose={() => setIsScheduleOpen(false)} title="Schedule Appointment" size="lg">
        <AppointmentForm
          defaultValues={{ clientId: id }}
          onSubmit={(formData) => scheduleMutation.mutate(formData)}
          onCancel={() => setIsScheduleOpen(false)}
          isSubmitting={scheduleMutation.isPending}
        />
      </Modal>

      {/* Delete Document Modal */}
      <Modal isOpen={!!docToDelete} onClose={() => setDocToDelete(null)} title="Delete document?" size="sm">
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
          &quot;{docToDelete?.fileName}&quot; will be permanently deleted and cannot be recovered.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setDocToDelete(null)} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => deleteDocMutation.mutate(docToDelete.id)}
            disabled={deleteDocMutation.isPending}
            className="btn btn-danger disabled:opacity-60"
          >
            {deleteDocMutation.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </Modal>

      <EligibilityCheckModal
        isOpen={isEligibilityOpen}
        onClose={() => {
          setIsEligibilityOpen(false);
          refetchEligibility();
        }}
        clientId={id}
        defaultPayerName={data?.insurancePayerName || ''}
        defaultPayerId={data?.insurancePayerEdiId || ''}
        defaultSubscriberId={data?.insuranceMemberId || ''}
      />

      {/* Archive Confirm Modal */}
      <Modal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} title="Archive client?" size="sm">
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
          This will archive {client.firstName} {client.lastName}. The record is kept but no longer active.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setIsDeleteOpen(false)} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="btn btn-danger disabled:opacity-60"
          >
            {deleteMutation.isPending ? 'Archiving…' : 'Archive'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
